<script setup lang="ts">
import SubmissionForm from "@/components/submission/SubmissionForm.vue";
import Regulations from "@/components/submission/Regulations.vue";
import Menu from "@/components/submission/Menu.vue";
import Prizes from "@/components/submission/Prizes.vue";
import Rules from "@/components/submission/Rules.vue";
import Training from "@/components/submission/Training.vue";
import Contact from "@/components/submission/Contact.vue";
import Ranking from "@/components/submission/Ranking.vue";

import {onBeforeUnmount, onMounted, ref} from "vue";
import background from "@/assets/images/backgrounds/bg.png";
import peroniLogo from "@/assets/images/hero/peroni_logo.webp";
import partnersLogo from "@/assets/images/hero/ferrari_logo.webp";
import ribbon from "@/assets/images/hero/szarfa.webp";
import ciao from "@/assets/images/hero/ciao.webp";
import emozioni from "@/assets/images/hero/emozioni.webp";
import zabkaBadge from "@/assets/images/hero/tylko_zabka.webp";
import bottle from "@/assets/images/hero/butelka.webp";
import people from "@/assets/images/hero/people.webp";

const crowdOffset = ref(0)
let parallaxFrame: number | null = null

const updateCrowdOffset = () => {
    crowdOffset.value = window.scrollY * 0.25
    parallaxFrame = null
}

const handleScroll = () => {
    if (parallaxFrame === null) {
        parallaxFrame = window.requestAnimationFrame(updateCrowdOffset)
    }
}

onMounted(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return
    }

    updateCrowdOffset()
    window.addEventListener('scroll', handleScroll, {passive: true})
})

onBeforeUnmount(() => {
    window.removeEventListener('scroll', handleScroll)

    if (parallaxFrame !== null) {
        window.cancelAnimationFrame(parallaxFrame)
    }
})
</script>

<template>
    <div id="home" class="hero">
        <div class="hero-menu">
            <Menu/>
        </div>

        <section
            class="hero-stage"
            :style="{ backgroundImage: `url(${background})` }"
            aria-label="Peroni Nastro Azzurro 0.0% - Ciao Emozioni"
        >
            <div class="hero-grid">
                <img
                    :src="peroniLogo"
                    alt="Peroni Nastro Azzurro 0.0%"
                    class="hero-peroni"
                >
                <img
                    :src="partnersLogo"
                    alt="Peroni Nastro Azzurro 0.0% - Ferrari Team Partner"
                    class="hero-partners"
                >
                <img
                    :src="ribbon"
                    alt=""
                    aria-hidden="true"
                    class="hero-ribbon"
                >
                <img :src="ciao" alt="Ciao" class="hero-ciao">
                <img :src="emozioni" alt="Emozioni" class="hero-emozioni">
                <img
                    :src="zabkaBadge"
                    alt="tylko w Żabka"
                    class="hero-zabka"
                >
                <img
                    :src="bottle"
                    alt="Dłoń trzymająca butelkę Peroni Nastro Azzurro 0.0%"
                    class="hero-bottle"
                >
<!--                <img-->
<!--                    :src="people"-->
<!--                    alt=""-->
<!--                    aria-hidden="true"-->
<!--                    class="hero-flag !z-1"-->
<!--                >-->
                <img
                    :src="people"
                    alt=""
                    aria-hidden="true"
                    class="hero-crowd"
                    :style="{ transform: `translate3d(0, ${crowdOffset}px, 0)` }"
                >
            </div>
        </section>
    </div>
    <Prizes></Prizes>
    <Rules></Rules>
    <div id="submission_training">
        <SubmissionForm></SubmissionForm>
        <Training></Training>
    </div>
    <Ranking></Ranking>
    <div id="regulations_contact">
        <Regulations></Regulations>
        <Contact></Contact>
    </div>
</template>

<style scoped>
.hero {
    width: 100%;
    overflow: hidden;
}

.hero-menu {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 20;
    width: 100%;
}

.hero-stage {
    position: relative;
    isolation: isolate;
    height: auto;
    aspect-ratio: 360 / 828;
    overflow: hidden;
    background-position: center center;
    background-repeat: no-repeat;
    background-size: cover;
}

.hero-grid {
    display: grid;
    position: absolute;
    inset: 0;
    grid-template-columns: repeat(24, minmax(0, 1fr));
    grid-template-rows: repeat(36, minmax(0, 1fr));
}

.hero-peroni,
.hero-partners,
.hero-ribbon,
.hero-ciao,
.hero-emozioni,
.hero-zabka,
.hero-bottle,
.hero-flag,
.hero-crowd {
    display: block;
    max-width: none;
    pointer-events: none;
    user-select: none;
}

.hero-peroni {
    grid-column: 7 / 19;
    grid-row: 6 / 10;
    z-index: 7;
    width: 100%;
    align-self: center;
    justify-self: center;
    animation: hero-fade-in 0.6s ease-out 0.1s both;
}

.hero-partners {
    grid-column: 8 / 23;
    grid-row: 23 / 25;
    z-index: 7;
    width: 100%;
    align-self: center;
    justify-self: center;
    transform: translateY(14px);
    animation: hero-fade-in 0.6s ease-out 0.25s both;
}

.hero-ribbon {
    position: absolute;
    top: 21%;
    left: -41%;
    z-index: 2;
    width: 176%;
}

.hero-ciao {
    grid-column: 5 / 20;
    grid-row: 10 / 19;
    z-index: 5;
    width: 100%;
    align-self: center;
    justify-self: center;
    transform: translateY(-8px);
    animation: hero-ciao-reveal 1s ease-out 0.7s both;
}

.hero-emozioni {
    grid-column: 5 / 21;
    grid-row: 19 / 22;
    z-index: 5;
    width: 100%;
    align-self: center;
    justify-self: center;
    transform-origin: center;
    animation: hero-emozioni-enter 1.1s ease-out 1s both;
}

.hero-zabka {
    display: none;
}

.hero-bottle {
    position: absolute;
    bottom: -1%;
    left: -2%;
    z-index: 6;
    height: 40%;
    animation: hero-bottle-enter 0.9s ease-out 0.3s both;
}

.hero-flag,
.hero-crowd {
    position: absolute;
    bottom: 0;
    left: -9%;
    z-index: 5;
    width: 175%;
}

.hero-flag {
    clip-path: inset(0 0 20% 0);
    animation: hero-flag-enter 0.8s ease-out 0.6s both;
}

:deep(.mobile-bar) {
    min-height: 96px;
    padding: 28px 30px;
}

:deep(.mobile-bar #nav-icon) {
    width: 54px;
    height: 36px;
    margin: 0;
}

:deep(.mobile-bar #nav-icon span) {
    height: 3px;
    background: #fff;
    border-radius: 0;
}

:deep(.mobile-bar #nav-icon span:nth-child(1)) {
    top: 0;
}

:deep(.mobile-bar #nav-icon span:nth-child(2)) {
    top: 15px;
}

:deep(.mobile-bar #nav-icon span:nth-child(3)) {
    top: 30px;
}

:deep(.mobile-bar .mobile-badge) {
    height: 39px;
    animation: hero-badge-enter 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) 1.3s both;
}

@media only screen and (min-width: 640px) and (max-width: 1023px) {
    .hero-stage {
        height: min(142vw, 1040px);
        aspect-ratio: auto;
    }

    .hero-peroni {
        grid-column: 9 / 17;
        grid-row: 5 / 9;
        max-width: 280px;
    }

    .hero-partners {
        grid-column: 9 / 19;
        grid-row: 23 / 25;
        max-width: 270px;
        transform: none;
    }

    .hero-ribbon {
        top: 28%;
        left: -20%;
        width: 140%;
    }

    .hero-ciao {
        grid-column: 7 / 18;
        grid-row: 11 / 18;
        max-width: 380px;
        transform: none;
    }

    .hero-emozioni {
        grid-column: 7 / 19;
        grid-row: 20 / 22;
        max-width: 350px;
    }

    .hero-bottle {
        left: 0;
        height: 43%;
    }

    .hero-flag,
    .hero-crowd {
        left: 0;
        width: 100%;
    }

    :deep(.mobile-bar) {
        min-height: 124px;
        padding: 38px 48px;
    }

    :deep(.mobile-bar #nav-icon) {
        width: 64px;
        height: 42px;
    }

    :deep(.mobile-bar #nav-icon span:nth-child(2)) {
        top: 18px;
    }

    :deep(.mobile-bar #nav-icon span:nth-child(3)) {
        top: 36px;
    }

    :deep(.mobile-bar .mobile-badge) {
        height: 46px;
    }
}

@media only screen and (min-width: 1024px) {
    .hero-menu {
        position: relative;
    }

    .hero-stage {
        height: auto;
        aspect-ratio: 1920 / 1040;
        background-position: center center;
    }

    .hero-grid {
        grid-template-rows: repeat(24, minmax(0, 1fr));
    }

    .hero-peroni {
        grid-column: 17 / 21;
        grid-row: 3 / 7;
        width: 100%;
        max-width: 315px;
    }

    .hero-partners {
        grid-column: 6 / 13;
        grid-row: 3 / 7;
        width: 100%;
        max-width: 540px;
        transform: none;
    }

    .hero-ribbon {
        top: 16%;
        left: 51%;
        width: 66%;
    }

    .hero-ciao {
        grid-column: 15 / 22;
        grid-row: 8 / 15;
        width: 100%;
        max-width: 560px;
        transform: none;
    }

    .hero-emozioni {
        grid-column: 15 / 22;
        grid-row: 16 / 19;
        width: 94%;
        max-width: 540px;
    }

    .hero-zabka {
        display: block;
        grid-column: 17 / 20;
        grid-row: 19 / 22;
        z-index: 6;
        width: 100%;
        max-width: 220px;
        align-self: center;
        justify-self: center;
        animation: hero-badge-enter 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) 1.3s both;
    }

    .hero-bottle {
        bottom: -1%;
        left: 3%;
        height: 101%;
    }

    .hero-flag,
    .hero-crowd {
        left: 0;
        width: 100%;
    }

    :deep(.desktop-menu) {
        min-height: 101px;
        gap: clamp(44px, 5.85vw, 112px);
        padding: 20px 24px;
        font-size: clamp(16px, 1.35vw, 26px);
        font-weight: 300;
    }
}

@keyframes hero-fade-in {
    from {
        opacity: 0;
    }

    to {
        opacity: 1;
    }
}

@keyframes hero-bottle-enter {
    from {
        opacity: 0;
        transform: translate3d(0, 40px, 0);
    }

    to {
        opacity: 1;
        transform: translate3d(0, 0, 0);
    }
}

@keyframes hero-flag-enter {
    from {
        opacity: 0;
        transform: translate3d(30px, -20px, 0);
    }

    to {
        opacity: 1;
        transform: translate3d(0, 0, 0);
    }
}

@keyframes hero-ciao-reveal {
    from {
        clip-path: inset(0 100% 0 0);
    }

    to {
        clip-path: inset(0 0 0 0);
    }
}

@keyframes hero-emozioni-enter {
    from {
        opacity: 0;
        transform: scaleX(0.9);
    }

    to {
        opacity: 1;
        transform: scaleX(1);
    }
}

@keyframes hero-badge-enter {
    from {
        opacity: 0;
        transform: scale(0.7);
    }

    to {
        opacity: 1;
        transform: scale(1);
    }
}

@media (prefers-reduced-motion: reduce) {
    .hero-peroni,
    .hero-partners,
    .hero-ciao,
    .hero-emozioni,
    .hero-zabka,
    .hero-bottle,
    .hero-flag,
    :deep(.mobile-bar .mobile-badge) {
        animation: none;
    }
}

#submission_training {
    background-image: url("../assets/images/backgrounds/bgTransparentPeopleMobile.webp");
    background-blend-mode: color-dodge;
    background-size: contain;
    background-position: bottom;
    background-repeat: no-repeat;
}

@media only screen and (min-width: 768px) {
    #submission_training {
        background-image: url("../assets/images/backgrounds/bgTransparentPeople.webp");
    }
}
#regulations_contact {
    background-image: url("../assets/images/backgrounds/bgSkyMobile.webp");
    background-blend-mode: color-dodge;
    background-size: cover;
    background-position: top;
    background-repeat: no-repeat;
}


@media only screen and (min-width: 1024px) {
    #regulations_contact {
        background-image: url("../assets/images/backgrounds/bgSky.webp");
    }
}
</style>
